export type ChoiceValue = string;
export type ChoiceStructure = {
  allowFreeFormEntry?: boolean;
  label?: string;
  children?: ChoiceStructure;
  value: ChoiceValue;
}[];

/**
 * choices is a nested structure of choices, where each choice has a value and
 * may have children. This function returns the maximum depth of the choices
 * structure. Presumably, one choice could have several layers of depth, where
 * others have fewer.
 * this method was originally written to inform the PaginatedDrawer of how many
 * steps it should show.
 */
export function getMaxDepth(choices: ChoiceStructure): number {
  if (!choices.length) return 0;

  return choices.reduce((max, choice) => {
    const childrenDepth = choice.children?.length
      ? getMaxDepth(choice.children) + 1
      : 0;
    return Math.max(max, childrenDepth);
  }, 1);
}

// based on the current value of the selection, traverse through the choices
// structure to find the next displayable set of choices
export function getChoicesForDisplay(
  choices: ChoiceStructure | undefined,
  selection: ChoiceValue[],
  depth = selection?.length ?? choices?.length ?? 0,
) {
  let choicesForDisplay = choices;
  const cappedDepth = Math.min(depth, selection.length);

  for (let i = 0; i < cappedDepth; i++) {
    const selected = selection[i];
    const choice = choicesForDisplay?.find((c) => c.value === selected);
    choicesForDisplay = choice?.children;
    if (!choicesForDisplay) break;
  }

  return choicesForDisplay;
}

import { TFunction } from "i18next";

export function getMoodCheckinChoices(t: TFunction): ChoiceStructure {
  return [
    {
      value: "Happy",
      label: t("telehealth-core:moodCheckin.happy"),
      children: [
        { value: "Content", label: t("telehealth-core:moodCheckin.content") },
        { value: "Proud", label: t("telehealth-core:moodCheckin.proud") },
        { value: "Accepted", label: t("telehealth-core:moodCheckin.accepted") },
        { value: "Peaceful", label: t("telehealth-core:moodCheckin.peaceful") },
        { value: "Trusting", label: t("telehealth-core:moodCheckin.trusting") },
        {
          value: "Optimistic",
          label: t("telehealth-core:moodCheckin.optimistic"),
        },
      ],
    },
    {
      value: "Sad",
      label: t("telehealth-core:moodCheckin.sad"),
      children: [
        { value: "Lonely", label: t("telehealth-core:moodCheckin.lonely") },
        {
          value: "Vulnerable",
          label: t("telehealth-core:moodCheckin.vulnerable"),
        },
        {
          value: "Despairing",
          label: t("telehealth-core:moodCheckin.despairing"),
        },
        { value: "Guilty", label: t("telehealth-core:moodCheckin.guilty") },
        {
          value: "Depressed",
          label: t("telehealth-core:moodCheckin.depressed"),
        },
        { value: "Hurt", label: t("telehealth-core:moodCheckin.hurt") },
      ],
    },
    {
      value: "Angry",
      label: t("telehealth-core:moodCheckin.angry"),
      children: [
        { value: "Let down", label: t("telehealth-core:moodCheckin.letDown") },
        {
          value: "Humiliated",
          label: t("telehealth-core:moodCheckin.humiliated"),
        },
        { value: "Bitter", label: t("telehealth-core:moodCheckin.bitter") },
        { value: "Mad", label: t("telehealth-core:moodCheckin.mad") },
        {
          value: "Frustrated",
          label: t("telehealth-core:moodCheckin.frustrated"),
        },
        { value: "Distant", label: t("telehealth-core:moodCheckin.distant") },
      ],
    },
    {
      value: "Afraid",
      label: t("telehealth-core:moodCheckin.afraid"),
      children: [
        { value: "Scared", label: t("telehealth-core:moodCheckin.scared") },
        { value: "Anxious", label: t("telehealth-core:moodCheckin.anxious") },
        { value: "Insecure", label: t("telehealth-core:moodCheckin.insecure") },
        { value: "Weak", label: t("telehealth-core:moodCheckin.weak") },
        { value: "Rejected", label: t("telehealth-core:moodCheckin.rejected") },
        {
          value: "Threatened",
          label: t("telehealth-core:moodCheckin.threatened"),
        },
      ],
    },
    {
      value: "Surprised",
      label: t("telehealth-core:moodCheckin.surprised"),
      children: [
        { value: "Startled", label: t("telehealth-core:moodCheckin.startled") },
        { value: "Confused", label: t("telehealth-core:moodCheckin.confused") },
        { value: "Amazed", label: t("telehealth-core:moodCheckin.amazed") },
        { value: "Excited", label: t("telehealth-core:moodCheckin.excited") },
      ],
    },
    {
      value: "Disgusted",
      label: t("telehealth-core:moodCheckin.disgusted"),
      children: [
        {
          value: "Disapproving",
          label: t("telehealth-core:moodCheckin.disapproving"),
        },
        {
          value: "Disappointed",
          label: t("telehealth-core:moodCheckin.disappointed"),
        },
        { value: "Awful", label: t("telehealth-core:moodCheckin.awful") },
        { value: "Repelled", label: t("telehealth-core:moodCheckin.repelled") },
      ],
    },
  ];
}
